/**
 * useHeadInjection — hängt JSON-LD, Meta-Tags oder Link-Tags DIREKT in
 * document.head an, umgeht damit react-helmet-async wenn mehrere Helmet-
 * Instanzen (App-Level + Page-Level) konkurrieren und sich gegenseitig
 * überschreiben. Cleanup entfernt die injizierten Nodes beim Unmount.
 *
 * Usage:
 *   useHeadJsonLd({ '@context': 'https://schema.org', '@type': 'Product', ... }, 'ld-product');
 *   useHeadMeta([{ property: 'og:type', content: 'product' }, { name: 'twitter:card', content: 'summary_large_image' }]);
 *   useHeadLink({ rel: 'canonical', href: 'https://…' }, 'canonical-product');
 */
import { useEffect } from 'react';

const MARKER_ATTR = 'data-hs-head';

export function useHeadJsonLd(obj, marker) {
  useEffect(() => {
    if (!obj || typeof obj !== 'object') return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute(MARKER_ATTR, marker || 'jsonld');
    try { el.textContent = JSON.stringify(obj); } catch { return undefined; }
    // Vorherige Node mit gleichem Marker entfernen, damit Route-Wechsel nicht dupliziert.
    document.head.querySelectorAll(`script[${MARKER_ATTR}="${marker}"]`).forEach((n) => n.remove());
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [obj, marker]);
}

export function useHeadMeta(list) {
  useEffect(() => {
    if (!Array.isArray(list) || list.length === 0) return undefined;
    const created = [];
    list.forEach(({ property, name, content }) => {
      if (!content) return;
      const attr = property ? 'property' : 'name';
      const key = property || name;
      // Vorhandene, konkurrierende Nodes ersetzen (ohne unsere Marker-Nodes zu doppeln)
      document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((n) => n.remove());
      const m = document.createElement('meta');
      m.setAttribute(attr, key);
      m.setAttribute('content', String(content));
      m.setAttribute(MARKER_ATTR, `meta-${key}`);
      document.head.appendChild(m);
      created.push(m);
    });
    return () => { created.forEach((n) => n.remove()); };
  }, [JSON.stringify(list)]);
}

export function useHeadLink({ rel, href } = {}, marker) {
  useEffect(() => {
    if (!rel || !href) return undefined;
    document.head.querySelectorAll(`link[rel="${rel}"][${MARKER_ATTR}]`).forEach((n) => n.remove());
    const l = document.createElement('link');
    l.setAttribute('rel', rel);
    l.setAttribute('href', href);
    l.setAttribute(MARKER_ATTR, marker || `link-${rel}`);
    document.head.appendChild(l);
    return () => { l.remove(); };
  }, [rel, href, marker]);
}
